import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bell, Globe, School } from 'lucide-react';

import { useLanguage } from '../context/LanguageContext';
import { API_URL } from '../lib/api';

function AppHeader({ user, onSessionExpired }) {
  const [noticeCount, setNoticeCount] = useState(0);
  const [schoolLogo, setSchoolLogo] = useState('');
  const navigate = useNavigate();
  const { language, toggleLanguage } = useLanguage();

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');

    const loadHeaderData = async () => {
      try {
        const requests = [
          axios.get(`${API_URL}/api/notices`),
          axios.get(`${API_URL}/api/schools`)
        ];

        if (token) {
          requests.push(axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          }));
        }

        const [noticesRes, schoolsRes, userRes] = await Promise.all(requests);
        if (cancelled) return;

        const activeUser = userRes?.data || user;
        const school = (schoolsRes.data || []).find((item) => (
          (activeUser?.school_id && item.id === activeUser.school_id) ||
          item.school_name === activeUser?.school_name
        ));

        setNoticeCount((noticesRes.data || []).length);
        setSchoolLogo(school?.school_logo || '');
      } catch (error) {
        if (!cancelled && error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          onSessionExpired?.();
          navigate('/login', { replace: true });
        }
      }
    };

    loadHeaderData();

    return () => {
      cancelled = true;
    };
  }, [navigate, onSessionExpired, user]);

  return (
    <header className="sticky top-0 z-50 border-b-2 border-zinc-200 bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          className="flex min-w-0 items-center gap-3 text-left"
          data-testid="app-header-home"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center">
            <img
              src="/monster-huddle-transparent.png"
              alt="Monster Huddle"
              className="h-full w-full object-contain drop-shadow-sm"
              data-testid="header-logo"
            />
          </span>
          <span className="truncate text-xl font-black text-zinc-900">Monster Huddle</span>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={toggleLanguage}
            className="flex items-center gap-1 rounded-lg p-2 transition-colors hover:bg-zinc-100"
            data-testid="language-toggle"
          >
            <Globe className="h-5 w-5" />
            <span className="text-sm font-bold">{language === 'en' ? '\u4e2d\u6587' : 'EN'}</span>
          </button>

          <button
            type="button"
            onClick={() => navigate('/notices')}
            className="relative rounded-lg p-2 transition-colors hover:bg-zinc-100"
            data-testid="notices-btn"
          >
            <Bell className="h-5 w-5" />
            {noticeCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
                {noticeCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="ml-1 transition-transform hover:scale-105"
            title={language === 'zh' ? '\u4e2a\u4eba\u8d44\u6599' : 'Profile'}
            data-testid="header-avatar-btn"
          >
            {schoolLogo ? (
              <img
                src={schoolLogo}
                alt={user?.school_name || 'School'}
                className="h-9 w-9 rounded-full bg-white object-contain ring-2 ring-violet-200"
              />
            ) : (
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100 ring-2 ring-violet-200">
                <School className="h-5 w-5 text-violet-600" />
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
